import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CardClient } from '@atlaskit/link-provider';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import {
	NEW_WHITEBOARD_QUERY_PARAM,
	PAYWALL_DEACTIVATION_IDENTIFIER,
	TEMPLATE_PICKER_QUERY_PARAM,
} from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import {
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
	EMBED_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import {
	fetchDataFromORS,
	IS_NEW_EMBED_QUERY_PARAM_KEY,
	normalizeAndValidateUrl,
} from '@confluence/embed-utils';
import {
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
} from '@confluence/template-utils/entry-points/urlUtils.ts';

import { appendQueryParams, processDraftUrl, getEditPageUrl } from '../helpers.ts';
import { CreateContentMutation } from '../queries/CreateContentMutation.graphql.ts';
import { CreateWhiteboardMutation } from '../queries/CreateWhiteboard.graphql.ts';
import { CreateDatabaseMutation } from '../queries/CreateDatabase.graphql.ts';
import { CreateSmartLinkEmbedMutation } from '../queries/CreateSmartLinkEmbed.graphql.ts';
import { CreateFolderMutation } from '../queries/CreateFolder.graphql.ts';
import type {
	CreateContentMutation as CreateContentMutationType,
	CreateContentMutationVariables,
	ContentSpecificCreateInput,
} from '../queries/__types__/CreateContentMutation.ts';
import {
	GraphQLContentStatus as PageGraphQLContentStatus,
	ConfluencePageSubType,
} from '../queries/__types__/CreateContentMutation.ts';
import type {
	CreateWhiteboardMutation as CreateWhiteboardMutationType,
	ContentSpecificCreateInput as CreateWhiteboardSpecificInput,
} from '../queries/__types__/CreateWhiteboardMutation.ts';
import type {
	CreateDatabaseMutation as CreateDatabaseType,
	CreateDatabaseMutationVariables,
} from '../queries/__types__/CreateDatabaseMutation.ts';
import type {
	ContentSpecificCreateInput as SmartLinkContentSpecificCreateInput,
	CreateSmartLinkEmbedMutation as CreateSmartLinkEmbedMutationType,
	CreateSmartLinkEmbedMutationVariables,
} from '../queries/__types__/CreateSmartLinkEmbedMutation.ts';
import type {
	CreateFolderMutation as CreateFolderMutationType,
	CreateFolderMutationVariables,
} from '../queries/__types__/CreateFolderMutation.ts';
import { GraphQLContentStatus } from '../queries/__types__/CreateSmartLinkEmbedMutation.ts';
import type { PaywallStatus } from '../createContentInputTypes.ts';

const NEW_DATABASE_QUERY_PARAM = 'isNewDatabase';

export type CreateContentResponse = {
	analyticsAttributes?: Record<string, any>;
	nonPrivacySafeAttributes?: Record<string, any>;
	contentId?: string | null;
	spaceId?: string | null;
	url?: string;
	collabService?: string | null;
	returnedSubType?: string | null;
	returnedStatus?: string | null;
};

export type createPageContentParams = {
	subType?: ConfluencePageSubType;
	spaceKey: string;
	parentPage?: string;
	title?: string;
	draftId?: string;
	linkCreation?: string;
	fabricEditorModeCacheStoreActions?: any;
	templateKey?: string;
	shouldOpenTemplatesGallery?: boolean;
	type: string;
};

export type createDatabaseParams = {
	spaceKey: string;
	parentId?: string;
};

export type createSmartLinkEmbedParams = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	createSource?: string;
	spaceKey?: string;
	parentId?: string;
	status?: string;
	embedUrl?: string;
};

export type createFolderParams = {
	spaceKey?: string;
	parentId?: string;
};

const _createWhiteboardMutation = async (
	variables: CreateWhiteboardMutationVariables,
): Promise<CreateWhiteboardMutationType> => {
	const {
		spaceKey,
		parentId,
		paywallStatus,
		deactivationIdentifier,
		restoreVersionContentId,
		title,
	} = variables;

	const contentSpecificCreateInput: CreateWhiteboardSpecificInput[] = [];

	if (paywallStatus) {
		contentSpecificCreateInput.push({
			key: 'defaultPaywallStatus',
			value: paywallStatus,
		});
	}

	if (deactivationIdentifier) {
		contentSpecificCreateInput.push({
			key: 'deactivationIdentifier',
			value: deactivationIdentifier,
		});
	}

	if (restoreVersionContentId) {
		contentSpecificCreateInput.push({
			key: 'restoreVersionContentId',
			value: restoreVersionContentId,
		});
	}

	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return (
		await getApolloClient().mutate({
			mutation: CreateWhiteboardMutation,
			variables: {
				spaceKey,
				parentId,
				contentSpecificCreateInput,
				title,
			},
			context: { single: true },
		})
	).data;
};
type CreateWhiteboardMutationVariables = {
	spaceKey: string;
	parentId?: string | null;
	deactivationIdentifier?: string;
	paywallStatus?: PaywallStatus;
	restoreVersionContentId?: string;
	title?: string;
};

export const createWhiteboardContent = async (
	mutationVariables: CreateWhiteboardMutationVariables,
	urlParams: object = {},
): Promise<CreateContentResponse> => {
	const {
		createContentContextual: { space, id: contentId },
	} = await _createWhiteboardMutation(mutationVariables);

	const { deactivationIdentifier, restoreVersionContentId } = mutationVariables || {};

	const url = WHITEBOARD_BOARD.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{
			query: {
				[NEW_WHITEBOARD_QUERY_PARAM]: true,
				[TEMPLATE_PICKER_QUERY_PARAM]: true,
				...urlParams,
				...(deactivationIdentifier && {
					[PAYWALL_DEACTIVATION_IDENTIFIER]: deactivationIdentifier,
				}),
				...(restoreVersionContentId && {
					flow: 'restoreVersion',
				}),
			},
		},
	);
	return {
		contentId,
		spaceId: space?.id,
		url,
		collabService: 'canvas-relay',
	};
};

const _createDatabaseMutation = async (
	variables: CreateDatabaseMutationVariables,
): Promise<CreateDatabaseType> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return (
		await getApolloClient().mutate({
			mutation: CreateDatabaseMutation,
			variables,
			context: { single: true },
		})
	).data;
};

export const createDatabaseContent = async ({
	spaceKey,
	parentId,
}: createDatabaseParams): Promise<CreateContentResponse> => {
	const {
		createContentContextual: { space, id: contentId },
	} = await _createDatabaseMutation({ spaceKey, parentId });

	const url = DATABASE_VIEW.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{
			query: {
				[NEW_DATABASE_QUERY_PARAM]: true,
			},
		},
	);
	return {
		contentId,
		spaceId: space?.id,
		url,
		collabService: 'canvas-relay',
	};
};

const _CreateSmartLinkEmbedMutation = async (
	variables: CreateSmartLinkEmbedMutationVariables,
): Promise<CreateSmartLinkEmbedMutationType> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return (
		await getApolloClient().mutate({
			mutation: CreateSmartLinkEmbedMutation,
			variables,
			context: { single: true },
		})
	).data;
};

export const createSmartLinkEmbedContent = async ({
	createAnalyticsEvent,
	createSource,
	spaceKey,
	parentId,
	embedUrl,
}: createSmartLinkEmbedParams): Promise<CreateContentResponse> => {
	let contentSpecificCreateInput: SmartLinkContentSpecificCreateInput[] | undefined;
	let product: string | undefined;
	let title: string | undefined;
	let embedExtensionKey: string | null | undefined;
	let embedResourceType: string | null | undefined;
	let normalizedEmbedUrl: string | null | undefined;

	if (embedUrl) {
		const cardClient = new CardClient();
		cardClient.setProduct('CONFLUENCE');
		normalizedEmbedUrl = normalizeAndValidateUrl(embedUrl);

		if (!normalizedEmbedUrl) {
			throw new Error('Invalid smart link embed URL');
		}

		const { meta, parsedEmbedData } = await fetchDataFromORS({
			cardClient,
			createAnalyticsEvent,
			embedUrl: normalizedEmbedUrl,
		});

		title = parsedEmbedData.title;
		product = parsedEmbedData.product || undefined;
		embedExtensionKey = meta?.extensionKey ?? null;
		embedResourceType = meta?.resourceType ?? null;

		contentSpecificCreateInput = [];

		if (parsedEmbedData.iconURL) {
			contentSpecificCreateInput.push({
				key: 'embedIconUrl',
				value: parsedEmbedData.iconURL,
			});
		}

		// We should use the URL returned by ORS if possible since it will return the URL in
		// the most normalized form (e.g. it can reformat URLs that contain unicode characters
		// such that BE will accept them)
		contentSpecificCreateInput.push({
			key: 'embedUrl',
			value: parsedEmbedData.url ?? normalizedEmbedUrl,
		});

		if (product) {
			contentSpecificCreateInput.push({ key: 'product', value: product });
		}

		if (createSource) {
			contentSpecificCreateInput.push({
				key: 'createSource',
				value: createSource,
			});
		}
	}

	// smart link embeds go through blank creation flow so no initial URL is provided to the mutation
	const {
		createContentContextual: { space, id: contentId },
	} = await _CreateSmartLinkEmbedMutation({
		...(contentSpecificCreateInput ? { contentSpecificCreateInput } : {}),
		...(title ? { title } : {}),
		type: 'embed',
		status: GraphQLContentStatus.CURRENT,
		parentId,
		spaceKey,
	});

	const query = {};

	// This query paramter is used to display the dot grid background when new *blank* embeds
	// are created. If there's an embedUrl already provided, it's not a blank embed.
	if (!embedUrl) {
		query[IS_NEW_EMBED_QUERY_PARAM_KEY] = true;
	}

	const url = EMBED_VIEW.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{ query },
	);

	return {
		analyticsAttributes: {
			extensionKey: embedExtensionKey,
			destinationObjectType: embedResourceType,
		},
		nonPrivacySafeAttributes: {
			linkDomain: product,
			url: normalizedEmbedUrl,
		},
		collabService: 'no-collab-for-smart-link-embeds',
		contentId,
		spaceId: space?.id,
		url,
	};
};

const _CreateFolderMutation = async (
	variables: CreateFolderMutationVariables,
): Promise<CreateFolderMutationType> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return (
		await getApolloClient().mutate({
			mutation: CreateFolderMutation,
			variables,
			context: { single: true },
		})
	).data;
};

export const createFolderContent = async ({
	spaceKey,
	parentId,
}: createFolderParams): Promise<CreateContentResponse> => {
	const {
		createContentContextual: { space, id: contentId },
	} = await _CreateFolderMutation({
		type: 'folder',
		status: GraphQLContentStatus.CURRENT,
		parentId,
		spaceKey,
	});

	return {
		contentId,
		spaceId: space?.id,
		collabService: 'no-collab-for-folders',
	};
};

const _createBlankContentMutation = async (
	variables: CreateContentMutationVariables,
): Promise<CreateContentMutationType> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return (
		await getApolloClient().mutate({
			mutation: CreateContentMutation,
			variables,
			context: { single: true },
		})
	).data;
};

const createContentGQL = async ({
	subType,
	spaceKey,
	parentPage,
	title,
	linkCreation,
	fabricEditorModeCacheStoreActions,
	templateKey,
	shouldOpenTemplatesGallery,
	type,
}: createPageContentParams): Promise<CreateContentResponse> => {
	const status =
		subType === ConfluencePageSubType.LIVE
			? PageGraphQLContentStatus.CURRENT
			: PageGraphQLContentStatus.DRAFT;

	const contentSpecificCreateInput: ContentSpecificCreateInput[] | undefined = templateKey
		? getTemplateContentSpecificInput(templateKey)
		: undefined;

	const variables: CreateContentMutationVariables = {
		subType,
		spaceKey,
		parentId: parentPage,
		title,
		status,
		contentSpecificCreateInput,
		type,
	};

	const pageData = await _createBlankContentMutation(variables);
	const contentId = pageData.createContentContextual?.id;
	const spaceId = pageData.createContentContextual?.space?.id;
	const editui = pageData.createContentContextual?.links?.editui || '';
	const collabService = pageData.createContentContextual?.metadata?.frontend?.collabService;
	const returnedSubType = pageData.createContentContextual?.subType;
	const returnedSpaceKey =
		pageData.createContentContextual?.space?.alias || pageData.createContentContextual?.space?.key;
	const context = pageData.createContentContextual?.links?.context || '';

	let url =
		subType === ConfluencePageSubType.LIVE
			? processDraftUrl(editui)
			: getEditPageUrl({
					spaceKey: returnedSpaceKey,
					contentId,
					url: context + editui,
					type,
				});

	if (linkCreation) {
		url = appendQueryParams(
			url,
			{ linkCreation },
			fabricEditorModeCacheStoreActions?.setFabricEditorMode,
		);
	}

	if (shouldOpenTemplatesGallery) {
		url = appendQueryParams(
			url,
			{ [IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_OPEN },
			fabricEditorModeCacheStoreActions?.setFabricEditorMode,
		);
	}

	return { contentId, spaceId, url, collabService, returnedSubType, returnedStatus: status };
};

export const createPageContent = async (
	args: createPageContentParams,
): Promise<CreateContentResponse> => {
	return createContentGQL({ ...args, type: 'page' });
};

export const createBlogContent = async (spaceKey: string): Promise<CreateContentResponse> => {
	return createContentGQL({ spaceKey, type: 'blogpost' });
};

// Template configuration mapping
const TEMPLATE_CONFIG = {
	'meeting-notes-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'projectplan-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'requirements-blueprint': {
		key: 'templateId',
		plugin: 'confluence-software-blueprints',
	},
	'marketing-campaign-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'marketing-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'design-decision-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'standard-operating-procedure-sop-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'business-status-update-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'kb-how-to-article-blueprint': {
		key: 'templateId',
		plugin: 'confluence-knowledge-base',
	},
	'fundraising-database-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'employee-handbook-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'hiring-process-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'business-strategy-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'account-planning-for-sales-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
} as const;

const TEMPLATE_REGEX =
	/^com\.atlassian\.confluence\.plugins\.confluence(-[0-9a-z]+)(-[0-9a-z]+)*-blueprints:([0-9a-z]+)(-[0-9a-z]+)*-blueprint$/;

/**
 * Generates the contentSpecificCreateInput for a given template key
 * @param templateKey - The template key (e.g., 'meeting-notes-blueprint')
 * @returns ContentSpecificCreateInput array or undefined if template not found
 */
const getTemplateContentSpecificInput = (
	templateKey: string,
): ContentSpecificCreateInput[] | undefined => {
	const config = TEMPLATE_CONFIG[templateKey as keyof typeof TEMPLATE_CONFIG];

	if (!config) {
		// Support full template keys
		if (TEMPLATE_REGEX.test(templateKey)) {
			return [
				{
					key: 'templateId',
					value: templateKey,
				},
			];
		}

		// Return undefined for unknown templates
		return undefined;
	}

	return [
		{
			key: config.key,
			value: `com.atlassian.confluence.plugins.${config.plugin}:${templateKey}`,
		},
	];
};
